import { useState } from 'react'

import { Button } from '@atoms'
import SearchField, { type SearchFieldProps } from '@molecules/SearchField'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<SearchFieldProps> = {
  displayName: { type: 'string', required: true, description: 'The label. It shows above the field.' },
  fieldName: {
    type: 'string',
    required: true,
    description: 'The input name, and the URL param an uncontrolled field reads its starting value from.',
  },
  onChange: {
    type: '(event) => void',
    required: true,
    description:
      'Called 300ms after the user stops typing, so a server search fires once per pause. With value set, it is called on every keystroke instead.',
  },
  value: {
    type: 'string',
    description:
      'Makes the field controlled, for filtering in the browser and for clearing the field from outside. Skips the 300ms wait, so never pair it with a server request.',
  },
  defaultValue: {
    type: 'string',
    description: 'Starting value of an uncontrolled field. Falls back to the fieldName URL param.',
  },
  placeholder: { type: 'string', description: 'Only alongside the visible label, never in place of it.' },
  hideLabel: {
    type: 'boolean',
    default: 'false',
    description: 'Keeps the label for screen readers only. Use when the context already names the search.',
  },
  inputWrapperClassName: { type: 'string', description: 'Classes for the bordered box around the input.' },
  dataCompId: { type: 'string', description: 'Sets data-compid for tests.' },
  ref: { type: 'Ref<HTMLInputElement>', description: 'The input element, for moving focus to it.' },
}

function Controlled() {
  const [query, setQuery] = useState('email')
  return (
    <div className="tw:flex tw:max-w-md tw:items-end tw:gap-3">
      <div className="tw:flex-1">
        <SearchField
          fieldName="faq_search"
          displayName="Search FAQs"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <Button rank="secondary" onClick={() => setQuery('')}>
        Clear search
      </Button>
    </div>
  )
}

const doc: ComponentDoc<SearchFieldProps> = {
  slug: 'search-field',
  name: 'SearchField',
  tier: 'molecule',
  description:
    'A text input with a search icon. By default it waits until typing pauses before calling onChange, for searches run on the server. Pass value for lists filtered in the browser.',
  importStatement: "import SearchField from '@molecules/SearchField'",
  sourcePath: 'app/frontend/components/molecules/SearchField.tsx',
  component: SearchField,
  props,
  examples: [
    {
      title: 'Server search',
      note: 'Uncontrolled. onChange fires once the user pauses, and the field starts from the URL param.',
      render: () => (
        <div className="tw:max-w-xs">
          <SearchField fieldName="search" displayName="Search groups" onChange={() => {}} />
        </div>
      ),
      code: `<SearchField
  fieldName="search"
  displayName="Search groups"
  onChange={(event) => searchFn(event, partialReloadKeys, setLoading)}
/>`,
    },
    {
      title: 'Filtering in the browser',
      note: 'Controlled, so a Clear search button can empty the field. This is the Help page.',
      render: () => <Controlled />,
      code: `const [query, setQuery] = useState('')

<SearchField
  fieldName="search"
  displayName="Search FAQs"
  value={query}
  onChange={(event) => setQuery(event.target.value)}
/>
<Button rank="secondary" onClick={() => setQuery('')}>Clear search</Button>`,
    },
  ],
  usage: {
    do: [
      'In a page header, follow the "With search" example on the Page header page.',
      'Pass value when the list is filtered in the browser or when something outside the field must clear it.',
    ],
    dont: [
      'Pass value for a server search. Every keystroke would send a request.',
      'Build a search from TextInput type="search". Use this so every search looks the same.',
    ],
  },
  a11y: [
    'The input has type="search", so it is announced as a search box and Escape clears it.',
    'The label stays visible unless hideLabel is set, and is then still read out.',
  ],
}

export default doc
